import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SparkleEffect } from '../../../components/SparkleEffect';
import { CheckIcon, RotateCcwIcon, DownloadIcon } from 'lucide-react';
// Define the checklist structure based on the uploaded image
const checklistData = [{
  id: 'vibe',
  title: 'SET THE VIBE.',
  time: '(5—10 min)',
  notes: "We're setting the tone first. Clean energy starts with how the space feels, not just how it looks.",
  tasks: [{
    id: 'vibe-1',
    text: 'Light a candle / open windows'
  }, {
    id: 'vibe-2',
    text: 'Put on a playlist or podcast'
  }, {
    id: 'vibe-3',
    text: 'Empty and load dishwasher'
  }, {
    id: 'vibe-4',
    text: 'Quick declutter of obvious mess'
  }, {
    id: 'vibe-5',
    text: 'Start a load of laundry'
  }]
}, {
  id: 'space',
  title: 'RESET YOUR SPACE',
  time: '(20—30 min)',
  notes: "Focus on visible areas first. If you see completion you feel like you've accomplished something.",
  tasks: [{
    id: 'space-1',
    text: 'Clear and wipe surfaces (kitchen, tables, bathroom)'
  }, {
    id: 'space-2',
    text: 'Take out trash'
  }, {
    id: 'space-3',
    text: 'Fold / switch laundry'
  }]
}, {
  id: 'comfort',
  title: 'RESET YOUR COMFORT ZONE',
  time: '(10—15 min)',
  notes: 'Your space should feel like peace again. This is your recharge zone',
  tasks: [{
    id: 'comfort-1',
    text: 'Make the bed and change pillows cases.'
  }, {
    id: 'comfort-2',
    text: 'Quick bedroom tidy'
  }, {
    id: 'comfort-3',
    text: 'Put away/ hang clothes'
  }, {
    id: 'comfort-4',
    text: 'Vacuum'
  }]
}, {
  id: 'bathroom',
  title: 'Reset Your Bathroom',
  time: '(10—15 min)',
  notes: 'Quick refresh, not a deep clean. A clean bathroom instantly makes everything feel more put together.',
  tasks: [{
    id: 'bath-1',
    text: 'pickup any thing out of place.'
  }, {
    id: 'bath-2',
    text: 'Wipe sink, shower or bath, and counter'
  }, {
    id: 'bath-3',
    text: 'Clean toilet (seat + quick bowl scrub)'
  }, {
    id: 'bath-4',
    text: 'Take out trash and sweep/mop floors'
  }]
}, {
  id: 'life',
  title: 'RESET YOUR LIFE',
  time: '(10—15 min)',
  notes: 'Get ahead of the week, not behind it. A little planning now saves a lot of stress later.',
  tasks: [{
    id: 'life-1',
    text: 'Plan meals / quick grocery list'
  }, {
    id: 'life-2',
    text: "Restock essentials and note what you're low on"
  }, {
    id: 'life-3',
    text: 'Set out clothes for the next day'
  }, {
    id: 'life-4',
    text: 'Check calendar for the week highlight top 3 priorities.'
  }]
}];
export function SundayResetPage() {
  // Load saved progress from localStorage or start fresh
  const [checkedTasks, setCheckedTasks] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem('sundayResetProgress');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {};
      }
    }
    return {};
  });
  // Save progress whenever it changes
  useEffect(() => {
    localStorage.setItem('sundayResetProgress', JSON.stringify(checkedTasks));
  }, [checkedTasks]);
  const toggleTask = (taskId: string) => {
    setCheckedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };
  const resetProgress = () => {
    if (window.confirm('Are you sure you want to uncheck all items and start over?')) {
      setCheckedTasks({});
    }
  };
  // Calculate progress
  const totalTasks = checklistData.reduce((acc, section) => acc + section.tasks.length, 0);
  const completedTasks = Object.values(checkedTasks).filter(Boolean).length;
  const progressPercentage = Math.round(completedTasks / totalTasks * 100);
  return <div className="min-h-screen pt-24 pb-24 relative bg-gradient-to-br from-[#fdf2f8] to-[#f3e8ff]" data-id="element-394">
      {/* Background styling matching the image vibe */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-[#fce7f3]/80 to-transparent pointer-events-none" data-id="element-395" />
      <div className="absolute top-20 left-10 w-64 h-64 bg-white/40 rounded-full blur-3xl pointer-events-none" data-id="element-396" />
      <div className="absolute top-40 right-10 w-96 h-96 bg-[#e0e7ff]/40 rounded-full blur-3xl pointer-events-none" data-id="element-397" />

      <SparkleEffect count={40} data-id="element-398" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-399">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-400">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-6xl font-bold text-[#3b426e] mb-4 uppercase tracking-wider" data-id="element-401">
            Sunday Reset Routine
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-lg text-plum-500 font-medium" data-id="element-402">
            Check things off as you go. Your progress saves automatically!
          </motion.p>
        </div>

        {/* Progress Bar & Controls */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white/60 backdrop-blur-md border border-white rounded-2xl p-6 shadow-sm mb-10 flex flex-col sm:flex-row items-center justify-between gap-6 sticky top-24 z-40" data-id="element-403">
          <div className="flex-1 w-full" data-id="element-404">
            <div className="flex justify-between text-sm font-bold text-[#3b426e] mb-2" data-id="element-405">
              <span data-id="element-406">Your Progress</span>
              <span data-id="element-407">
                {progressPercentage}% ({completedTasks}/{totalTasks})
              </span>
            </div>
            <div className="h-3 w-full bg-plum-100 rounded-full overflow-hidden" data-id="element-408">
              <motion.div className="h-full bg-gradient-to-r from-cta-400 to-cta-500" initial={{
              width: 0
            }} animate={{
              width: `${progressPercentage}%`
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} data-id="element-409" />
            </div>
          </div>

          <button onClick={resetProgress} className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-plum-100 text-plum-600 font-bold text-sm hover:bg-plum-200 transition-colors" data-id="element-410">
            <RotateCcwIcon className="w-4 h-4" data-id="element-411" />
            Start Fresh
          </button>
        </motion.div>

        {/* Checklist Sections */}
        <div className="space-y-8" data-id="element-412">
          {checklistData.map((section, sectionIdx) => <motion.div key={section.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3 + sectionIdx * 0.1
        }} className="bg-white/70 backdrop-blur-sm border border-plum-100 rounded-2xl overflow-hidden shadow-sm flex flex-col md:flex-row" data-id="element-413">
              {/* Section Header (Left side on desktop) */}
              <div className="bg-[#fce7f3]/50 p-6 md:w-1/3 flex flex-col justify-center items-center text-center border-b md:border-b-0 md:border-r border-plum-100" data-id="element-414">
                <h2 className="font-heading text-2xl font-bold text-[#3b426e] uppercase tracking-wide mb-2" data-id="element-415">
                  {section.title}
                </h2>
                <p className="text-plum-500 font-bold text-lg" data-id="element-416">
                  {section.time}
                </p>
              </div>

              {/* Tasks and Notes (Right side on desktop) */}
              <div className="md:w-2/3 flex flex-col" data-id="element-417">
                {/* Tasks List */}
                <div className="p-0" data-id="element-418">
                  {section.tasks.map((task, taskIdx) => {
                const isChecked = checkedTasks[task.id] || false;
                return <label key={task.id} className={`flex items-start gap-4 p-4 cursor-pointer transition-colors border-b border-plum-50 ${isChecked ? 'bg-white/40' : 'hover:bg-white/50'}`} data-id="element-419">
                        <div className="relative flex items-center justify-center shrink-0 mt-0.5" data-id="element-420">
                          <input type="checkbox" className="peer sr-only" checked={isChecked} onChange={() => toggleTask(task.id)} data-id="element-421" />
                          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${isChecked ? 'bg-cta-500 border-cta-500' : 'border-plum-300 bg-white'}`} data-id="element-422">
                            <motion.div initial={false} animate={{
                        scale: isChecked ? 1 : 0,
                        opacity: isChecked ? 1 : 0
                      }} transition={{
                        duration: 0.2
                      }} data-id="element-423">
                              <CheckIcon className="w-4 h-4 text-white" data-id="element-424" />
                            </motion.div>
                          </div>
                        </div>
                        <span className={`text-lg transition-all duration-300 ${isChecked ? 'text-plum-400 line-through' : 'text-plum-700 font-medium'}`} data-id="element-425">
                          {task.text}
                        </span>
                      </label>;
              })}
                </div>

                {/* Notes Section */}
                <div className="p-6 bg-[#f8fafc]/50 mt-auto border-t border-plum-100" data-id="element-426">
                  <h3 className="text-xs font-bold text-plum-400 uppercase tracking-widest mb-2 text-center" data-id="element-427">
                    Notes
                  </h3>
                  <p className="text-sm text-plum-600 text-center italic font-medium" data-id="element-428">
                    {section.notes}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Upsell CTA at the bottom */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-16 bg-gradient-to-br from-cta-500 to-plum-600 rounded-3xl p-8 md:p-12 text-center text-white shadow-xl relative overflow-hidden" data-id="element-429">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-430" />

          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 relative z-10" data-id="element-431">
            Want the printable version?
          </h2>
          <p className="text-plum-100 text-lg mb-8 max-w-2xl mx-auto relative z-10 font-medium" data-id="element-432">
            Get the beautiful PDF to print for your fridge, plus the editable
            Canva template so you can customize these tasks for your exact life.
          </p>

          <a href="https://trippinwithmandy.gumroad.com/l/puiyqk" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-cta-600 font-bold text-lg hover:bg-plum-50 transition-colors shadow-lg relative z-10" data-id="element-433">
            <DownloadIcon className="w-5 h-5" data-id="element-434" />
            Get the Template ($2.99)
          </a>
        </motion.div>
      </div>
    </div>;
}